import { z } from 'zod';

// ============================================
// COMMON VALIDATION PATTERNS
// ============================================

// Email validation
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address')
  .max(254, 'Email must be less than 254 characters');

// Password validation (NIST-inspired: 12+ chars, mixed case, number, special char)
export const passwordSchema = z
  .string()
  .min(12, 'Password must be at least 12 characters')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^a-zA-Z0-9]/, 'Password must contain at least one special character');

// US Phone validation (10 digits)
export const phoneSchema = z
  .string()
  .min(1, 'Phone number is required')
  .regex(
    /^\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
    'Please enter a valid 10-digit phone number'
  );

// Optional phone (allows empty string)
export const optionalPhoneSchema = z
  .string()
  .regex(
    /^$|^\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
    'Please enter a valid 10-digit phone number'
  )
  .optional()
  .or(z.literal(''));

// URL validation (only allow http/https protocols)
export const urlSchema = z
  .string()
  .refine(
    (val) => {
      if (val === '') return true; // Allow empty
      try {
        const url = new URL(val);
        return ['http:', 'https:'].includes(url.protocol);
      } catch {
        return false;
      }
    },
    { message: 'Please enter a valid URL (http or https)' }
  )
  .or(z.literal(''));

// Hex color validation
export const hexColorSchema = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, 'Please enter a valid hex color (e.g., #2563eb)');

// ============================================
// AUTH SCHEMAS
// ============================================

export const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

export const signUpSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export const onboardingSchema = z.object({
  companyName: z
    .string()
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters')
    .regex(/^[a-zA-Z0-9\s\-&'.]+$/, 'Company name contains invalid characters'),
});

// ============================================
// ONBOARDING WIZARD SCHEMAS
// ============================================

// Step 1: Company Basics
export const companyBasicsStepSchema = z.object({
  companyName: z
    .string()
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters')
    .regex(/^[a-zA-Z0-9\s\-&'.]+$/, 'Company name contains invalid characters'),
  businessPhone: z
    .string()
    .regex(
      /^$|^\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
      'Please enter a valid 10-digit phone number'
    )
    .optional()
    .or(z.literal('')),
  primaryColor: hexColorSchema,
});

// Step 2: Widget Config
export const widgetConfigStepSchema = z.object({
  showQuote: z.boolean(),
  requireAddress: z.boolean(),
  requireTimeline: z.boolean(),
});

// Step 3: Lead Capture
export const leadCaptureStepSchema = z.object({
  ctaBookCallUrl: z.string().url('Please enter a valid URL').or(z.literal('')),
  ctaGetQuoteUrl: z.string().url('Please enter a valid URL').or(z.literal('')),
});

// Complete wizard data
export const onboardingWizardSchema = z.object({
  companyName: z
    .string()
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters'),
  businessPhone: z.string().optional().or(z.literal('')),
  primaryColor: hexColorSchema,
  showQuote: z.boolean(),
  requireAddress: z.boolean(),
  requireTimeline: z.boolean(),
  ctaBookCallUrl: z.string().optional().or(z.literal('')),
  ctaGetQuoteUrl: z.string().optional().or(z.literal('')),
});

// ============================================
// SETTINGS SCHEMAS
// ============================================

export const profileSchema = z.object({
  displayName: z
    .string()
    .min(1, 'Display name is required')
    .max(50, 'Display name must be less than 50 characters'),
  email: emailSchema,
});

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: 'New password must be different from current password',
    path: ['newPassword'],
  });

export const companySettingsSchema = z.object({
  companyName: z
    .string()
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters'),
  primaryColor: hexColorSchema,
});

export const widgetSettingsSchema = z.object({
  enabled: z.boolean(),
  cta_book_call_url: urlSchema.optional().or(z.literal('')),
  cta_get_quote_url: urlSchema.optional().or(z.literal('')),
  business_phone: optionalPhoneSchema,
  watermark_text: z
    .string()
    .max(50, 'Watermark text must be less than 50 characters')
    .optional()
    .or(z.literal('')),
});

// Pricing validation - positive numbers with reasonable bounds
const baseCostSchema = z
  .number({ invalid_type_error: 'Must be a number' })
  .min(100, 'Minimum base cost is $100')
  .max(500000, 'Maximum base cost is $500,000');

const multiplierSchema = z
  .number({ invalid_type_error: 'Must be a number' })
  .min(0.1, 'Minimum multiplier is 0.1')
  .max(10, 'Maximum multiplier is 10');

export const pricingConfigSchema = z.object({
  base_costs: z.object({
    kitchen: baseCostSchema,
    bathroom: baseCostSchema,
    basement: baseCostSchema,
    living_room: baseCostSchema,
    bedroom: baseCostSchema,
    outdoor: baseCostSchema,
    garage: baseCostSchema,
    home_office: baseCostSchema,
  }),
  size_multipliers: z.object({
    small: multiplierSchema,
    medium: multiplierSchema,
    large: multiplierSchema,
  }),
  condition_multipliers: z.object({
    good: multiplierSchema,
    fair: multiplierSchema,
    poor: multiplierSchema,
  }),
  selection_costs: z.object({
    flooring: z.object({
      standard: baseCostSchema,
      premium: baseCostSchema,
    }),
    walls: z.object({
      paint: baseCostSchema,
      wallpaper: baseCostSchema,
      tile: baseCostSchema,
    }),
    cabinets: z.object({
      refinish: baseCostSchema,
      replace: baseCostSchema,
    }),
    countertops: z.object({
      laminate: baseCostSchema,
      quartz: baseCostSchema,
      granite: baseCostSchema,
    }),
    lighting: z.object({
      basic: baseCostSchema,
      recessed: baseCostSchema,
      custom: baseCostSchema,
    }),
    fixtures: z.object({
      standard: baseCostSchema,
      premium: baseCostSchema,
    }),
  }),
});

// ============================================
// LEAD SCHEMAS
// ============================================

export const newLeadSchema = z.object({
  clientName: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters'),
  clientPhone: phoneSchema,
  roomType: z.enum(['kitchen', 'bathroom', 'basement', 'bedroom', 'living_room', 'other'], {
    errorMap: () => ({ message: 'Please select a room type' }),
  }),
  roomState: z.enum(
    ['needs_full_renovation', 'needs_partial_update', 'cosmetic_refresh', 'new_construction'],
    {
      errorMap: () => ({ message: 'Please select current state' }),
    }
  ),
  materials: z
    .string()
    .max(500, 'Materials must be less than 500 characters')
    .optional()
    .or(z.literal('')),
  notes: z
    .string()
    .max(500, 'Notes must be less than 500 characters')
    .optional()
    .or(z.literal('')),
});

// ============================================
// WIDGET (CLIENT-FACING) SCHEMAS
// ============================================

export const contactFormSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be less than 50 characters'),
  email: emailSchema,
});

export const phoneVerificationSchema = z.object({
  phone: phoneSchema,
});

export const otpSchema = z.object({
  code: z
    .string()
    .length(6, 'Verification code must be 6 digits')
    .regex(/^[0-9]+$/, 'Code must contain only numbers'),
});

// ============================================
// CUSTOM COLOR SCHEMAS
// ============================================

/**
 * Sherwin-Williams color code validation (e.g., SW-7029)
 * Auto-converts to uppercase format
 */
export const swColorCodeSchema = z
  .string()
  .min(1, 'Color code is required')
  .transform((val) => val.toUpperCase())
  .pipe(
    z
      .string()
      .regex(/^SW-\d{4}$/, 'Invalid format. Use SW-XXXX (e.g., SW-7029)')
  );

/**
 * Hex color with auto-prepend of # if missing
 * Accepts with or without leading #, outputs with #
 */
export const hexColorWithTransformSchema = z
  .string()
  .min(1, 'Hex color is required')
  .transform((val) => {
    const trimmed = val.trim();
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  })
  .pipe(
    z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color. Use format #RRGGBB')
  );

/**
 * Color name validation with sanitization
 * Allows alphanumeric, spaces, and safe special characters
 */
export const colorNameSchema = z
  .string()
  .min(1, 'Color name is required')
  .max(50, 'Color name must be less than 50 characters')
  .regex(
    /^[a-zA-Z0-9\s&\-'"%()]+$/,
    'Color name contains invalid characters. Only letters, numbers, spaces, and & - \' " % ( ) are allowed'
  )
  .transform((val) => val.trim());

/**
 * Color family enum validation
 * Must match one of the predefined Sherwin-Williams color families
 */
export const colorFamilySchema = z.enum(
  ['White', 'Gray', 'Greige', 'Blue', 'Green', 'Neutral', 'Warm', 'Black'],
  {
    errorMap: () => ({ message: 'Please select a valid color family' }),
  }
);

/**
 * Light Reflectance Value (LRV) validation
 * Optional field, range 0-100, can be decimal
 */
export const lrvSchema = z
  .number({ invalid_type_error: 'LRV must be a number' })
  .min(0, 'LRV must be between 0 and 100')
  .max(100, 'LRV must be between 0 and 100')
  .optional();

/**
 * Complete custom color object schema
 * Used for adding custom Sherwin-Williams colors to tenant palette
 */
export const customColorSchema = z.object({
  code: swColorCodeSchema,
  name: colorNameSchema,
  hex: hexColorWithTransformSchema,
  family: colorFamilySchema,
  lrv: lrvSchema,
});

/**
 * Array of custom colors with maximum limit
 * Prevents excessive custom colors per tenant
 */
export const customColorsArraySchema = z
  .array(customColorSchema)
  .max(50, 'Maximum 50 custom colors allowed');

/**
 * Helper function to validate a custom color
 * Returns validation result with parsed data or error details
 */
export function validateCustomColor(data: unknown): {
  success: boolean;
  data?: z.infer<typeof customColorSchema>;
  errors?: Array<{ field: string; message: string }>;
} {
  const result = customColorSchema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  const errors = result.error.errors.map((err) => ({
    field: err.path.join('.'),
    message: err.message,
  }));

  return { success: false, errors };
}

// ============================================
// TYPE EXPORTS
// ============================================

export type SignInFormData = z.infer<typeof signInSchema>;
export type SignUpFormData = z.infer<typeof signUpSchema>;
export type OnboardingFormData = z.infer<typeof onboardingSchema>;
export type CompanyBasicsStepData = z.infer<typeof companyBasicsStepSchema>;
export type WidgetConfigStepData = z.infer<typeof widgetConfigStepSchema>;
export type LeadCaptureStepData = z.infer<typeof leadCaptureStepSchema>;
export type OnboardingWizardFormData = z.infer<typeof onboardingWizardSchema>;
export type ProfileFormData = z.infer<typeof profileSchema>;
export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;
export type CompanySettingsFormData = z.infer<typeof companySettingsSchema>;
export type WidgetSettingsFormData = z.infer<typeof widgetSettingsSchema>;
export type PricingConfigFormData = z.infer<typeof pricingConfigSchema>;
export type NewLeadFormData = z.infer<typeof newLeadSchema>;
export type ContactFormData = z.infer<typeof contactFormSchema>;
export type PhoneVerificationFormData = z.infer<typeof phoneVerificationSchema>;
export type OtpFormData = z.infer<typeof otpSchema>;
export type CustomColorFormData = z.infer<typeof customColorSchema>;
export type CustomColorsArrayData = z.infer<typeof customColorsArraySchema>;
